import { useMemo, useState } from 'react';
import { getSupabaseClient } from '../../data/supabase-client';
import { useAppUser } from '../../data/queries/use-app-user';
import {
  describeTimeZoneSaveFailure,
  isStaleTimeZoneSave,
  setAppUserTimeZone,
} from '../../data/writers/set-app-user-timezone';
import {
  describeTimeZone,
  describeTimeZoneOffset,
  isAcceptableTimeZone,
  readDeviceTimeZone,
  readSupportedTimeZones,
} from '../../lib/timezone';

/**
 * TIME ZONE — M12 / S12-00.
 *
 * **CREWLINK HAS ONE TIMEZONE PER PERSON, AND THIS IS THE ONLY PLACE IT
 * CHANGES.** It decides what "today" means and how every stored instant is
 * rendered (D-009, PT §15, INV-T17). Until this section existed it was seeded
 * from the device at sign-in and then frozen forever, which was right for
 * someone who never moves and wrong for everyone else.
 *
 * **A DEVICE TIMEZONE CHANGE MUST NEVER MOVE IT, AND NOTHING HERE LETS IT.**
 * The device's zone is offered as a SUGGESTION when it differs from the saved
 * one — a button he may press — and pressing it only fills the picker.
 * **Nothing saves without an explicit Save.** A phone that flies to Manila
 * changes nothing until Brandon says so, because a timestamp silently changing
 * meaning is worse than a timestamp being inconvenient.
 *
 * **THE IDENTIFIER IS THE VALUE; THE DESCRIPTION IS DECORATION.**
 * `America/New_York` is stored and displayed. *"Eastern Daylight Time"* and
 * *"GMT-4"* sit beside it because they are what a person recognises — and
 * **neither is ever stored**, because an abbreviation is ambiguous worldwide
 * and an offset is wrong twice a year.
 */
export function TimeZoneSection() {
  const { status, profile, error, reload } = useAppUser();

  const [picked, setPicked] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  /** Read once. The list is large and does not change while a screen is open. */
  const zones = useMemo(() => readSupportedTimeZones(), []);
  const deviceZone = useMemo(() => readDeviceTimeZone(), []);

  const savedZone = profile?.timezone ?? null;
  // Server truth until he picks something; then his choice, until he saves.
  const selected = picked ?? savedZone ?? '';
  const isDirty = savedZone !== null && selected !== '' && selected !== savedZone;

  /**
   * **THE DEVICE IS A SUGGESTION, AND ONLY WHEN IT DISAGREES.** Offering
   * "use the phone's zone" when it already matches would be a button that does
   * nothing, and a nudge toward a change nobody needs.
   *
   * **AND ONLY WHEN IT IS SOMETHING CREWLINK COULD ACTUALLY SAVE.**
   * `readDeviceTimeZone` substitutes `UTC` when the runtime reports nothing
   * usable, but a runtime CAN report `EST` — and offering a value the server is
   * known to refuse is offering a dead end. The suggestion is checked against
   * the same acceptance category the picker uses.
   */
  const suggestDevice =
    savedZone !== null &&
    isAcceptableTimeZone(deviceZone) &&
    deviceZone !== savedZone &&
    deviceZone !== selected;

  async function save(): Promise<void> {
    const client = getSupabaseClient();
    if (!client || savedZone === null || selected === '' || selected === savedZone) return;

    setBusy(true);
    setNotice(null);
    setSaved(false);
    try {
      // **THE EXPECTED VALUE IS WHAT THIS SCREEN WAS SHOWING.** If it moved
      // somewhere else since the read, the database refuses rather than
      // overwriting a newer choice.
      await setAppUserTimeZone(client, savedZone, selected);
      // **THE SCREEN FOLLOWS THE SERVER, NOT THE CLICK.** Re-reading is what
      // makes "Saved" mean saved: an optimistic label here would be
      // indistinguishable from success at exactly the moment it was wrong.
      setPicked(null);
      await reload();
      setSaved(true);
    } catch (caught) {
      setNotice(describeTimeZoneSaveFailure(caught));

      // **A STALE REFUSAL MEANS THIS SCREEN IS SHOWING AN OBSOLETE VALUE**, and
      // saying so while still displaying the old one would be telling him to
      // look at something that is already wrong. So the profile is re-read and
      // the true saved zone replaces it, **and his own choice is cleared** —
      // keeping it would leave the picker arguing with the value above it.
      //
      // Every other refusal leaves the picker exactly as it was, so he does not
      // have to find his choice again. **Neither case shows success.**
      if (isStaleTimeZoneSave(caught)) {
        setPicked(null);
        await reload();
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="settings-section" aria-labelledby="settings-timezone-heading">
      <h2 id="settings-timezone-heading">Time zone</h2>
      <p className="settings-lead">
        Crewlink uses this to decide what “today” means and to show every time it has saved.
        Changing your phone’s time zone does not change it — only this does.
      </p>

      {status === 'loading' ? <p className="settings-lead">Loading…</p> : null}

      {status === 'failed' ? (
        <p className="cl-error" role="alert">
          {error ?? 'Crewlink could not load your settings.'}
        </p>
      ) : null}

      {status === 'ready' && savedZone === null ? (
        <p className="settings-lead">
          Crewlink has not saved a time zone for you yet. It is set when you sign in.
        </p>
      ) : null}

      {status === 'ready' && savedZone !== null ? (
        <>
          <p className="settings-timezone-current">
            <span className="settings-timezone-zone">{savedZone}</span>
            <TimeZoneDescription zone={savedZone} />
          </p>

          <label className="cl-field" htmlFor="settings-timezone-select">
            <span className="cl-label">Change time zone</span>
          </label>

          {zones.length > 0 ? (
            <select
              id="settings-timezone-select"
              className="cl-input"
              value={selected}
              disabled={busy}
              onChange={(event) => {
                setSaved(false);
                setNotice(null);
                setPicked(event.target.value);
              }}
            >
              {/* The saved zone is always offered, even if this runtime's list
                  somehow lacks it — otherwise the picker would silently show a
                  different zone than the one in force. */}
              {zones.includes(savedZone) ? null : <option value={savedZone}>{savedZone}</option>}
              {zones.map((zone) => (
                <option key={zone} value={zone}>
                  {zone}
                </option>
              ))}
            </select>
          ) : (
            /* **NO LIST IS A REAL STATE, NOT A BROKEN ONE.** An older WebView
               cannot enumerate zones, and an empty menu would be worse than a
               field. The server validates either way. */
            <input
              id="settings-timezone-select"
              className="cl-input"
              type="text"
              value={selected}
              disabled={busy}
              spellCheck={false}
              autoCapitalize="none"
              placeholder="America/New_York"
              onChange={(event) => {
                setSaved(false);
                setNotice(null);
                setPicked(event.target.value);
              }}
            />
          )}

          {selected !== savedZone && selected !== '' ? (
            <p className="settings-timezone-preview">
              <span className="settings-timezone-zone">{selected}</span>
              <TimeZoneDescription zone={selected} />
            </p>
          ) : null}

          {suggestDevice ? (
            <p className="settings-logo-actions">
              <button
                type="button"
                className="cl-button cl-button--quiet"
                disabled={busy}
                onClick={() => {
                  setSaved(false);
                  setNotice(null);
                  setPicked(deviceZone);
                }}
              >
                Use this device’s time zone ({deviceZone})
              </button>
            </p>
          ) : null}

          <p className="settings-logo-actions">
            <button
              type="button"
              className="cl-button"
              disabled={busy || !isDirty}
              onClick={() => {
                void save();
              }}
            >
              {busy ? 'Saving…' : 'Save time zone'}
            </button>
          </p>

          {notice ? (
            <p className="cl-error" role="alert">
              {notice}
            </p>
          ) : null}

          {saved ? (
            <p className="settings-saved" role="status">
              Time zone saved.
            </p>
          ) : null}
        </>
      ) : null}
    </section>
  );
}

/**
 * The friendly half of the label. **Null when the runtime cannot describe the
 * zone** — the identifier stands alone rather than being given an invented
 * name.
 */
function TimeZoneDescription({ zone }: { readonly zone: string }) {
  const description = describeTimeZone(zone);
  const offset = describeTimeZoneOffset(zone);
  const parts = [description, offset].filter((part): part is string => part !== null);
  if (parts.length === 0) return null;
  return <span className="settings-timezone-meta">{parts.join(' · ')}</span>;
}
